// Libraries imports
import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import Grid from '@mui/material/Grid';
import Input from "@mui/material/Input";
import IconButton from '@mui/material/IconButton';
import InputAdornment from "@mui/material/InputAdornment";

// Theme, assets and utils imports
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";
import { THEME_MODE } from '../../../constants/Theme';
import { utils } from "../../../lib/utils";

// Component imports
import SettingTextField from "../../../components/SettingTextField";

// Local components and utils
import SectionHeader from './SectionHeader';
import ImageColorComponent from "./ImageColorComponent";
import { FIELD_TYPES, getTemplateKey } from "./constants";

const defaultColor = "#000000";

const ColorInput = ({ value, setValue }) => {
    const [color, setColor] = useState(value);
    const timerRef = useRef();

    const handleInput = (val) => {
        setColor(val.toUpperCase());
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setValue(val.toUpperCase());
        }, 250);
    }

    useEffect(() => {
        if (color !== value) setColor(value);
    }, [value]);

    return <Input
        type="color"
        sx={{ width: '2rem', marginRight: '0.75rem', padding: 0, '& input': { height: '2em', borderRadius: '12px' } }}
        value={color ?? defaultColor}
        onChange={(e) => handleInput(e.target.value)}
    />
}

const EmailTemplate = ({ title, showSection, toggleSection, templateData, handleChange, uploadFile, isUploading, themeMode, errors, setErrors, fieldKey }) => {
    const theme_mode = THEME_MODE[themeMode];
    const styles = componentStyles(theme_mode);

    const onInputChange = (field, value, fieldType) => {
        if (fieldType === FIELD_TYPES.color) {
            const errorKey = getTemplateKey(fieldKey, field);
            if (!value && errors[errorKey]) {
                const newErrors = { ...errors };
                delete newErrors[errorKey];
                setErrors(newErrors);
            } else if (value) {
                if (!utils.isValidHexColor(value)) {
                    console.log('%cInvalid Color: ', 'color: white; background: red;')
                    setErrors({ ...errors, [getTemplateKey(fieldKey, field)]: "Invalid hex color code" });
                } else {
                    const newErrors = { ...errors };
                    delete newErrors[errorKey];
                    setErrors(newErrors);
                }
            }
        }
        handleChange(field, value, fieldKey);
    }

    const onFileUpload = (field, file) => {
        uploadFile(field, file, fieldKey);
    }

    const isHeaderUploading = !!isUploading && isUploading.fieldKey === fieldKey && isUploading.field === "headerImage";
    const isFooterUploading = !!isUploading && isUploading.fieldKey === fieldKey && isUploading.field === "footerImage";

    return (
        <Grid container item xs={12} spacing={2} >
            <SectionHeader
                theme_mode={theme_mode}
                title={title}
                onClick={() => toggleSection(!showSection)}
                showSection={showSection}
                styles={styles}
            />

            <Grid container item xs={12} spacing={2} sx={{ display: showSection ? 'flex' : 'none' }}>
                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <ImageColorComponent
                        value={{
                            image: templateData.headerImage,
                            color: templateData.headerBgColor,
                        }}
                        onChange={onInputChange}
                        label="Header Background"
                        styles={styles}
                        imageFieldKey={"headerImage"}
                        colorFieldKey={"headerBgColor"}
                        error={errors[getTemplateKey(fieldKey, "headerImage")] || errors[getTemplateKey(fieldKey, "headerBgColor")]}
                        uploadFile={onFileUpload}
                        isLoading={isHeaderUploading}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.headerTitle}
                        setValue={(val) => onInputChange("headerTitle", val)}
                        required={true}
                        label={"Header Title"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "headerTitle")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.headerTitleColor ?? ""}
                        setValue={(val) => onInputChange("headerTitleColor", val, FIELD_TYPES.color)}
                        required={true}
                        label={"Header Title Color"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "headerTitleColor")]}
                        leftView={<ColorInput
                            value={templateData.headerTitleColor ?? "#000000"}
                            setValue={(val) => onInputChange("headerTitleColor", val, FIELD_TYPES.color)}
                        />}

                        rightView={<InputAdornment position="end">
                            <IconButton onClick={() => onInputChange("headerTitleColor", null, FIELD_TYPES.color)}>
                                <DeleteIcon width={22} height={22} viewBox='0 0 22 22' />
                            </IconButton>
                        </InputAdornment>}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.headerSubTitle}
                        setValue={(val) => onInputChange("headerSubTitle", val)}
                        required={true}
                        label={"Header Subtitle"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "headerSubTitle")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.headerSubTitleColor ?? ""}
                        setValue={(val) => onInputChange("headerSubTitleColor", val, FIELD_TYPES.color)}
                        required={true}
                        label={"Header Subtitle Color"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "headerSubTitleColor")]}
                        leftView={<ColorInput
                            value={templateData.headerSubTitleColor}
                            setValue={(val) => onInputChange("headerSubTitleColor", val, FIELD_TYPES.color)}
                        />}

                        rightView={<InputAdornment position="end">
                            <IconButton onClick={() => onInputChange("headerSubTitleColor", null, FIELD_TYPES.color)}>
                                <DeleteIcon width={22} height={22} viewBox='0 0 22 22' />
                            </IconButton>
                        </InputAdornment>}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.helpText}
                        setValue={(val) => onInputChange("helpText", val)}
                        required={true}
                        label={"Help Text"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "helpText")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.collectionInfoHeading}
                        setValue={(val) => onInputChange("collectionInfoHeading", val)}
                        required={true}
                        label={"Collection Info Heading"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "collectionInfoHeading")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.collectionInfoText}
                        setValue={(val) => onInputChange("collectionInfoText", val)}
                        required={true}
                        label={"Collection Info Text"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "collectionInfoText")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <ImageColorComponent
                        value={{
                            image: templateData.footerImage,
                            color: templateData.footerBackgroundColor,
                        }}
                        onChange={onInputChange}
                        label="Footer Background"
                        styles={styles}
                        imageFieldKey={"footerImage"}
                        colorFieldKey={"footerBackgroundColor"}
                        error={errors[getTemplateKey(fieldKey, "footerBackground")]}
                        uploadFile={onFileUpload}
                        isLoading={isFooterUploading}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.footerTitle}
                        setValue={(val) => onInputChange("footerTitle", val)}
                        required={true}
                        label={"Footer Title"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "footerTitle")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.footerTitleColor ?? ""}
                        setValue={(val) => onInputChange("footerTitleColor", val, FIELD_TYPES.color)}
                        required={true}
                        label={"Footer Title Color"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "footerTitleColor")]}
                        leftView={<ColorInput
                            value={templateData.footerTitleColor}
                            setValue={(val) => onInputChange("footerTitleColor", val, FIELD_TYPES.color)}
                        />}

                        rightView={<InputAdornment position="end">
                            <IconButton onClick={() => onInputChange("footerTitleColor", null, FIELD_TYPES.color)}>
                                <DeleteIcon width={22} height={22} viewBox='0 0 22 22' />
                            </IconButton>
                        </InputAdornment>}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.footerSubTitle}
                        setValue={(val) => onInputChange("footerSubTitle", val)}
                        placeholder={""}
                        required={true}
                        label={"Footer Subtitle"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "footerSubTitle")]}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.footerSubTitleColor ?? ""}
                        setValue={(val) => onInputChange("footerSubTitleColor", val, FIELD_TYPES.color)}
                        placeholder={""}
                        required={true}
                        label={"Footer Subtitle Color"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "footerSubTitleColor")]}
                        leftView={<ColorInput
                            value={templateData.footerSubTitleColor}
                            setValue={(val) => onInputChange("footerSubTitleColor", val)}
                        />}

                        rightView={<InputAdornment position="end">
                            <IconButton onClick={() => onInputChange("footerSubTitleColor", null, FIELD_TYPES.color)}>
                                <DeleteIcon width={22} height={22} viewBox='0 0 22 22' />
                            </IconButton>
                        </InputAdornment>}
                    />
                </Grid>

                <Grid item xs={12} md={6} lg={4} xl={3} mb={2}>
                    <SettingTextField
                        value={templateData.packageField}
                        setValue={(val) => onInputChange("packageField", val, FIELD_TYPES.color)}
                        placeholder={""}
                        required={false}
                        label={"Packaging Field"}
                        labelStyle={styles.labelStyle}
                        border={"1px solid #AAA"}
                        variant={"filled"}
                        error={errors[getTemplateKey(fieldKey, "packageField")]}
                    />
                </Grid>

            </Grid>
        </Grid>
    )
}

const mapStateToProps = (state) => ({
    themeMode: state.userData.themeMode
});

export default connect(mapStateToProps)(EmailTemplate);

const componentStyles = (Theme) => ({
    labelStyle: {
        fontSize: 14,
        fontFamily: "GilroySemiBold",
    },
    saveBtn: {
        backgroundColor: Theme.btnBg4,
        color: Theme.btnText2,
        ml: 1,
        "&:hover": {
            backgroundColor: `${Theme.btnBg4} !important`,
        },
    },
    cancelBtn: {
        backgroundColor: Theme.btnBg3,
        border: `2px solid ${Theme.btnBg5Border}`,
        color: Theme.text,
        mr: 1,
        "&:hover": {
            backgroundColor: `${Theme.btnBg4} !important`,
        },
    },
});
